/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UiButton, UiHStack, UiSpinner, UiStack, UiText, UiVStack } from '@/lib/ui';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import Header from '@/registration/component/Register/Layout/Header';
import { generatePageUrl } from '@/app/pages';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { type AuthObject, type LoginRequest, login, registrationQueryKey } from '@/api/registration';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseMessageBarError from '@/base/MessageBar/Error';

interface LandingPageProps {
}

const loginErrorMessage = 'Your login link has expired or has already been used. Please go to the email page to sign in.';
const noUserSessionErrorMessage = 'You are not signed in. Please go to the email page to sign in.';

const LandingPage: FC<LandingPageProps> = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const client = searchParams.get('client');
  const uid = searchParams.get('uid');

  const { eventId, tenantCode } = useRegisterRoute();
  const { userSession, setUserSession } = useUserSession();
  const { createTenantApiRequest } = useTenantApi();
  const { toNextStep } = useRegisterSteps();
  const [error, setError] = useState<string>('');
  const registrationQuery = useRegistrationQuery(eventId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { reportToGlobal } = useApiErrorHandler();

  const { data, mutate } = useMutation<ApiResponseSingle<AuthObject>, Error, LoginRequest>({
    mutationFn: async (loginData: LoginRequest) => {
      return await login(createTenantApiRequest)(loginData);
    },
    onError: (loginError: Error) => {
      setUserSession(undefined);
      setError(loginErrorMessage);
      reportToGlobal(loginError);
    }
  });

  const emailPageUrl = useMemo(
    () => {
      return generatePageUrl('RegistrationRegisterEmail', { tenantCode, eventId });
    },
    [eventId, tenantCode]
  );

  const onBackEmail = useCallback(() => {
    return navigate(emailPageUrl);
  }, [emailPageUrl, navigate]);

  useEffect(() => {
    if (token && uid && client) {
      setUserSession(undefined);
      void queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }] });
      mutate({ token, client, uid });
    }
  }, [client, token, uid, mutate]);

  useEffect(() => {
    if (data?.item?.uid) {
      setUserSession({ email: data.item.uid, client: data.item.client, authToken: data.item.token });
      void registrationQuery.refetch();
    } else if (data?.errors && data?.errors?.length > 0) {
      setUserSession(undefined);
      setError(data.errors.join(', '));
    } else {
      reportToGlobal(new Error('Landing page: Unhandled login error'));
    }
  }, [data, reportToGlobal, setUserSession]);

  useEffect(() => {
    if (!token && !userSession) {
      setError(noUserSessionErrorMessage);
    }
  }, [token, userSession]);

  useEffect(() => {
    const hasValidUserSessionBeenUpdated = token && data?.item;
    const noLoginRequired = !token && userSession?.authToken;

    if ((hasValidUserSessionBeenUpdated ?? noLoginRequired) && registrationQuery?.data) {
      toNextStep();
    }
  }, [token, toNextStep, userSession?.authToken, data?.item, registrationQuery?.data]);

  if (error) {
    return (
      <RegisterLayout header={<Header />}>
        <UiStack spacing={4}>
          {/*<UiText fontWeight={600} variant="h4">User authentication failed</UiText>*/}
          <BaseMessageBarError>
            User authentication failed. {error}
          </BaseMessageBarError>
          {/*<UiText variant='h6'>{error}</UiText>*/}
          <UiHStack justifyContent={'flex-end'}>
            <UiButton colorScheme={'primary'} mt={5} onClick={onBackEmail}>
              Back to login
            </UiButton>
          </UiHStack>
        </UiStack>
      </RegisterLayout>
    );
  }

  return (
    <RegisterLayout header={<Header />}>
      <UiStack>
        <UiSpinner />
      </UiStack>
    </RegisterLayout>
  );
};

const UserSessionLayout: FC<LandingPageProps> = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const client = searchParams.get('client');
  const uid = searchParams.get('uid');

  const { userSession, setUserSession } = useUserSession();

  if (!token || !client || !uid) {
    return <LandingPage />;
  }

  if (uid !== userSession?.email) {
    setUserSession(undefined);
  }

  return <LandingPage />;
};

export default UserSessionLayout;
